<template>
  <div>
    <!-- No HQ for this year, and not yet adding: prompt to create -->
    <div v-if="!hq && !editing" class="text-center py-5">
      <p class="text-secondary mb-3">No HQ budget exists for {{ selectedYear }}.</p>
      <button class="btn btn-primary" @click="startEdit">
        <i class="fa-solid fa-plus me-1" /> Add HQ budget for {{ selectedYear }}
      </button>
    </div>

    <!-- Otherwise show the figures (read-only until the user chooses to edit) -->
    <div v-else>
      <div class="d-flex justify-content-end mb-3">
        <template v-if="editing">
          <button class="btn btn-link btn-sm me-2" @click="cancelEdit" :disabled="isProcessing">Cancel</button>
          <button class="btn btn-sm btn-primary" @click="save" :disabled="isProcessing">
            <div class="spinner-border spinner-border-sm" v-if="isProcessing"></div>
            <span v-else>{{ hq ? "Update budget" : "Create budget" }}</span>
          </button>
        </template>
        <button v-else class="btn btn-sm btn-primary" @click="startEdit">
          <i class="fas fa-edit me-1" /> Update budget
        </button>
      </div>

      <!-- EDIT MODE: editable input grid + running totals -->
      <template v-if="editing">
        <div class="row g-3">
          <div class="col-md-6" v-for="cat in HQ_LINE_CATEGORIES" :key="cat">
            <label class="small mb-1">{{ CATEGORY_LABELS[cat] }}</label>
            <div class="input-group">
              <span class="input-group-text">RM</span>
              <input
                type="text" inputmode="decimal" class="form-control text-end"
                placeholder="0.00" :value="displayAmount(cat)"
                @input="onAmountInput(cat, $event)"
                @focus="focusedCat = cat"
                @blur="focusedCat = null"
              />
            </div>
          </div>
        </div>

        <div class="mt-4 p-3 rounded bg-semi-transparent">
          <div class="d-flex justify-content-between"><span>WPS total</span><strong>{{ formatMoney(wpsTotalValue) }}</strong></div>
          <div class="d-flex justify-content-between"><span>SST (8%)</span><strong>{{ formatMoney(sstValue) }}</strong></div>
          <hr />
          <div class="d-flex justify-content-between fs-5"><span>Yearly total</span><strong>{{ formatMoney(yearlyTotalValue) }}</strong></div>
        </div>
      </template>

      <!-- VIEW MODE: grouped contract table (matches the KJP NILAI KONTRAK layout) -->
      <div v-else class="table-responsive">
        <table class="table table-bordered table-dark align-middle mb-0">
          <thead>
            <tr>
              <th>KONTRAK</th>
              <th class="text-end">Nilai Kontrak (RM)</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in contractTopRows" :key="row.category">
              <td class="fw-semibold">{{ row.label }}</td>
              <td class="text-end">{{ fmt2(amounts[row.category]) }}</td>
            </tr>

            <!-- WPS: expandable group (expanded by default), total shown on the header -->
            <tr style="cursor: pointer" @click="wpsExpanded = !wpsExpanded">
              <td class="fw-semibold">
                <i class="fas me-2" :class="wpsExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                Wang Peruntukan Sementara (WPS)
              </td>
              <td class="text-end fw-semibold">{{ fmt2(wpsTotalValue) }}</td>
            </tr>
            <template v-if="wpsExpanded">
              <tr v-for="row in contractWpsRows" :key="row.category">
                <td class="ps-5 text-secondary">{{ row.label }}</td>
                <td class="text-end">{{ fmt2(amounts[row.category]) }}</td>
              </tr>
            </template>

            <tr class="fw-bold">
              <td class="text-end">Jumlah Keseluruhan</td>
              <td class="text-end">{{ fmt2(subtotalValue) }}</td>
            </tr>
            <tr class="fw-bold">
              <td class="text-end">SST (8%)</td>
              <td class="text-end">{{ fmt2(sstValue) }}</td>
            </tr>
            <tr class="fw-bold table-active">
              <td class="text-end">NILAI KONTRAK TAHUNAN</td>
              <td class="text-end">{{ fmt2(yearlyTotalValue) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import {
  HQ_LINE_CATEGORIES,
  CATEGORY_LABELS,
  wpsTotal,
  sst,
  yearlyTotal,
  formatAmountInput,
  parseAmountInput,
  formatAmount2,
} from "@/utils/budget";

export default {
  name: "hq-budget-tab",
  props: ["selectedYear", "hq", "lines"],
  data() {
    return {
      HQ_LINE_CATEGORIES,
      CATEGORY_LABELS,
      isProcessing: false,
      editing: false,
      focusedCat: null,
      wpsExpanded: true,
      amounts: this.blankAmounts(),
      // Read-only contract view layout (official KJP wording + ordering).
      contractTopRows: [
        { label: "Kos Awalan Kontrak", category: "kos_awalan" },
        { label: "Kerja Penyelenggaraan Rutin", category: "kerja_rutin" },
      ],
      contractWpsRows: [
        { label: "Periodic Maintenance Works", category: "wps_nod" },
        { label: "Emergency Works", category: "wps_emergency" },
        { label: "Pavement Condition Assessment", category: "wps_pca" },
        { label: "Transfer of Technology", category: "wps_tot" },
        { label: "Electrical Maintenance Works", category: "wps_electrical" },
        { label: "Integrated Road Management System", category: "wps_irms" },
      ],
    };
  },
  watch: {
    lines: {
      handler() {
        this.syncFromLines();
        // A fresh load (initial, post-save, or year change) returns to read-only view.
        this.editing = false;
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    formatMoney() {
      return (val) => this.Helper.formatMoney(val);
    },
    linesFromAmounts() {
      return HQ_LINE_CATEGORIES.map((c) => ({ category: c, amount: this.amounts[c] || 0 }));
    },
    wpsTotalValue() {
      return wpsTotal(this.linesFromAmounts);
    },
    sstValue() {
      return sst(this.linesFromAmounts);
    },
    yearlyTotalValue() {
      return yearlyTotal(this.linesFromAmounts);
    },
    // Jumlah Keseluruhan (a+b+c) = kos_awalan + kerja_rutin + WPS total (pre-SST).
    subtotalValue() {
      const m = this.amounts;
      return (Number(m.kos_awalan) || 0) + (Number(m.kerja_rutin) || 0) + this.wpsTotalValue;
    },
  },
  methods: {
    blankAmounts() {
      // null (not 0) so inputs render empty — the user types into a blank field
      // instead of having to clear a leading 0. Empty coerces back to 0 on save.
      const a = {};
      HQ_LINE_CATEGORIES.forEach((c) => (a[c] = null));
      return a;
    },
    syncFromLines() {
      const a = this.blankAmounts();
      (this.lines || []).forEach((l) => {
        if (a[l.category] !== undefined) a[l.category] = Number(l.amount) || null;
      });
      this.amounts = a;
    },
    // At rest, show a consistent 2-decimal amount (e.g. "20,000,000.00"). Only while
    // editing AND focused, show the free-typing format so decimals aren't forced mid-edit.
    displayAmount(cat) {
      const v = this.amounts[cat];
      if (v == null || v === "") return "";
      return this.editing && this.focusedCat === cat
        ? formatAmountInput(String(v))
        : formatAmount2(v);
    },
    // Read-only display formatter (always 2 decimals; null/empty -> "0.00").
    fmt2(v) {
      return formatAmount2(v || 0);
    },
    startEdit() {
      this.syncFromLines(); // ensure fields reflect the current saved values
      this.editing = true;
    },
    cancelEdit() {
      this.syncFromLines(); // discard unsaved edits
      this.editing = false;
    },
    // Re-format on input (thousands separators, one dot, max 2 decimals) and store the
    // parsed number. Setting the field value forces invalid chars (e, +, …) out.
    onAmountInput(cat, event) {
      const formatted = formatAmountInput(event.target.value);
      this.amounts[cat] = parseAmountInput(formatted);
      event.target.value = formatted;
    },
    // Create-or-update in one path: create the HQ if missing, then upsert all 8 lines
    // (PUT existing, POST any that don't exist yet) with the entered amounts.
    async save() {
      this.isProcessing = true;
      const wasNew = !this.hq;
      try {
        let hqId = this.hq && this.hq.id;
        if (!hqId) {
          const year = this.selectedYear || this.Helper.currentYear;
          const hqRes = await this.API.post("nod-budget-hqs", { year });
          hqId = hqRes.data.id;
        }

        const existing = {};
        (this.lines || []).forEach((l) => {
          existing[l.category] = l;
        });

        await Promise.all(
          HQ_LINE_CATEGORIES.map((category) => {
            const amount = this.amounts[category] || 0;
            const line = existing[category];
            return line
              ? this.API.put(`nod-budget-lines/${line.id}`, { amount })
              : this.API.post("nod-budget-lines", { hq: hqId, category, amount });
          })
        );

        this.editing = false;
        this.$emit("saved");
        Swal.fire(wasNew ? "HQ budget created" : "HQ budget updated", "", "success");
      } catch (e) {
        console.error(e);
        Swal.fire(wasNew ? "Error creating HQ budget" : "Error updating HQ budget", "", "error");
      }
      this.isProcessing = false;
    },
  },
};
</script>
